import React, { useState, useEffect } from 'react';
import { FiAlertCircle, FiX, FiArrowUp, FiAlertTriangle } from 'react-icons/fi';
import { useUsageDashboard } from '../../hooks/useUsageDashboard';
import { getPlanName } from '../../hooks/useSubscription';
import { useNavigate } from 'react-router-dom';

interface QuotaAlertProps {
  /**
   * Position de l'alerte sur l'écran
   */
  position?: 'top' | 'bottom';

  /**
   * Afficher uniquement les alertes de sévérité haute ou moyenne
   */
  minSeverity?: 'low' | 'medium' | 'high';

  /**
   * Permettre la fermeture manuelle de l'alerte
   */
  dismissible?: boolean;

  /**
   * Classe CSS personnalisée
   */
  className?: string;
}

/**
 * Composant d'alerte de quotas
 *
 * Affiche automatiquement les alertes de quota et de trial
 * Peut être placé n'importe où dans l'application pour prévenir l'utilisateur
 *
 * @example
 * ```tsx
 * // Dans le Dashboard
 * <QuotaAlert position="top" dismissible />
 *
 * // Dans une page spécifique
 * <QuotaAlert minSeverity="high" />
 * ```
 */
const QuotaAlert: React.FC<QuotaAlertProps> = ({
  position = 'top',
  minSeverity = 'medium',
  dismissible = true,
  className = ''
}) => {
  const { subscription, trial, alerts, loading } = useUsageDashboard();
  const navigate = useNavigate();
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set());

  // Charger les alertes rejetées depuis le localStorage
  useEffect(() => {
    const stored = localStorage.getItem('dismissed_quota_alerts');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setDismissedAlerts(new Set(parsed));
      } catch (e) {
        console.error('Failed to parse dismissed alerts:', e);
      }
    }
  }, []);

  // Sauvegarder les alertes rejetées dans le localStorage
  useEffect(() => {
    if (dismissedAlerts.size > 0) {
      localStorage.setItem('dismissed_quota_alerts', JSON.stringify(Array.from(dismissedAlerts)));
    }
  }, [dismissedAlerts]);

  const handleDismiss = (alertKey: string) => {
    if (dismissible) {
      setDismissedAlerts((prev) => new Set(prev).add(alertKey));
    }
  };

  const handleUpgrade = () => {
    navigate('/settings/subscription');
  };

  if (loading || !subscription) {
    return null;
  }

  // Filtrer les alertes selon la sévérité minimale
  const filteredAlerts = alerts?.filter((alert) => {
    if (minSeverity === 'high') {
      return alert.severity === 'high';
    }
    if (minSeverity === 'medium') {
      return alert.severity === 'high' || alert.severity === 'medium';
    }
    return true;
  }) || [];

  // Ajouter l'alerte de trial si nécessaire
  const allAlerts = [...filteredAlerts];
  if (trial && subscription.plan === 'free') {
    if (trial.is_expired) {
      allAlerts.unshift({
        resource: 'trial',
        message: 'Votre période d\'essai gratuite a expiré. Passez à un plan payant pour continuer.',
        severity: 'high'
      });
    } else if (trial.is_expiring_soon && trial.days_remaining <= 3) {
      allAlerts.unshift({
        resource: 'trial',
        message: `Votre période d'essai expire dans ${trial.days_remaining} jour(s). Passez à un plan payant maintenant.`,
        severity: 'high'
      });
    } else if (trial.is_expiring_soon && trial.days_remaining <= 7) {
      allAlerts.unshift({
        resource: 'trial',
        message: `Votre période d'essai expire dans ${trial.days_remaining} jours.`,
        severity: 'medium'
      });
    }
  }

  // Filtrer les alertes déjà rejetées
  const visibleAlerts = allAlerts.filter((alert) => {
    const alertKey = `${alert.resource}-${alert.message}`;
    return !dismissedAlerts.has(alertKey);
  });

  if (visibleAlerts.length === 0) {
    return null;
  }

  const positionClasses = position === 'top'
    ? 'top-0 left-0 right-0'
    : 'bottom-0 left-0 right-0';

  return (
    <div className={`${positionClasses} z-40 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 py-3 space-y-2">
        {visibleAlerts.map((alert, idx) => {
          const alertKey = `${alert.resource}-${alert.message}`;
          const isTrialAlert = alert.resource === 'trial';

          return (
            <div
              key={alertKey}
              className={`rounded-lg shadow-lg border flex items-start gap-3 p-4 ${
                alert.severity === 'high'
                  ? 'bg-red-50 border-red-300'
                  : alert.severity === 'medium'
                  ? 'bg-orange-50 border-orange-300'
                  : 'bg-yellow-50 border-yellow-300'
              }`}
            >
              <div
                className={`p-2 rounded-full flex-shrink-0 ${
                  alert.severity === 'high'
                    ? 'bg-red-100'
                    : alert.severity === 'medium'
                    ? 'bg-orange-100'
                    : 'bg-yellow-100'
                }`}
              >
                {alert.severity === 'high' ? (
                  <FiAlertCircle className="text-red-600" size={20} />
                ) : (
                  <FiAlertTriangle
                    className={
                      alert.severity === 'medium'
                        ? 'text-orange-600'
                        : 'text-yellow-600'
                    }
                    size={20}
                  />
                )}
              </div>

              <div className="flex-1 min-w-0">
                <p
                  className={`font-medium ${
                    alert.severity === 'high'
                      ? 'text-red-900'
                      : alert.severity === 'medium'
                      ? 'text-orange-900'
                      : 'text-yellow-900'
                  }`}
                >
                  {alert.message}
                </p>
                {alert.current !== undefined && alert.limit !== undefined && (
                  <p
                    className={`text-sm mt-1 ${
                      alert.severity === 'high'
                        ? 'text-red-700'
                        : alert.severity === 'medium'
                        ? 'text-orange-700'
                        : 'text-yellow-700'
                    }`}
                  >
                    Utilisation : {alert.current} / {alert.limit}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {(isTrialAlert || alert.severity === 'high') && (
                  <button
                    onClick={handleUpgrade}
                    className={`px-4 py-2 rounded-lg font-medium transition flex items-center gap-2 ${
                      alert.severity === 'high'
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-orange-600 text-white hover:bg-orange-700'
                    }`}
                  >
                    <FiArrowUp size={16} />
                    Mettre à niveau
                  </button>
                )}

                {dismissible && (
                  <button
                    onClick={() => handleDismiss(alertKey)}
                    className={`p-2 rounded-lg transition ${
                      alert.severity === 'high'
                        ? 'hover:bg-red-100 text-red-600'
                        : alert.severity === 'medium'
                        ? 'hover:bg-orange-100 text-orange-600'
                        : 'hover:bg-yellow-100 text-yellow-600'
                    }`}
                    title="Ignorer"
                  >
                    <FiX size={20} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuotaAlert;

/**
 * Version compacte du QuotaAlert pour affichage dans une barre latérale ou un header
 */
export const QuotaAlertCompact: React.FC<{
  onClick?: () => void;
}> = ({ onClick }) => {
  const { subscription, trial, alerts, loading } = useUsageDashboard();

  if (loading || !subscription) {
    return null;
  }

  const hasHighSeverityAlert = alerts?.some((a) => a.severity === 'high');
  const hasTrialExpiring = trial && trial.is_expiring_soon && trial.days_remaining <= 3;
  const hasTrialExpired = trial && trial.is_expired;

  if (!hasHighSeverityAlert && !hasTrialExpiring && !hasTrialExpired) {
    return null;
  }

  const count = (alerts?.filter((a) => a.severity === 'high').length || 0) +
                (hasTrialExpiring || hasTrialExpired ? 1 : 0);

  return (
    <button
      onClick={onClick}
      className="relative p-2 rounded-lg hover:bg-red-50 transition group"
      title={`${count} alerte(s) de quota`}
    >
      <FiAlertCircle className="text-red-600" size={24} />
      {count > 0 && (
        <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
          {count}
        </span>
      )}
    </button>
  );
};
